/**
 * ─────────────────────────────────────────────────────────────────
 * Hook reutilizable para compartir contenido.
 *
 * Comportamiento por plataforma:
 *  - Móvil (Capacitor): abre el menú nativo de compartir del sistema
 *    (WhatsApp, email, Bluetooth, Drive, etc.)
 *  - Web: usa window.print() como fallback
 *
 * Uso:
 *   const { compartir, compartiendo } = useCompartir();
 *   await compartir({ titulo: "Mi lista", texto: "..." });
 * ─────────────────────────────────────────────────────────────────
 */
import { useState } from "react";
import { Capacitor } from "@capacitor/core";

export function useCompartir() {
    const [compartiendo, setCompartiendo] = useState(false);

    /**
     * Comparte contenido usando el método más adecuado para la plataforma.
     * @param {object} opciones
     * @param {string} opciones.titulo  - Título del contenido a compartir
     * @param {string} opciones.texto   - Texto con el contenido formateado
     * @param {string} [opciones.url]   - URL opcional a incluir
     */
    const compartir = async ({ titulo, texto, url }) => {
        setCompartiendo(true);
        try {
            if (Capacitor.isNativePlatform()) {
                // En móvil: menú nativo del sistema operativo
                const { Share } = await import("@capacitor/share");
                await Share.share({
                    title:         titulo,
                    text:          texto,
                    url:           url,
                    dialogTitle:   "Compartir lista",
                });
            } else {
                // En web: imprimir como PDF
                window.print();
            }
        } catch (err) {
            // El usuario canceló el share — no es un error real
            if (!err?.message?.includes("cancel")) {
                console.error("Error al compartir:", err);
            }
        } finally {
            setCompartiendo(false);
        }
    };

    return { compartir, compartiendo };
}

/**
 * Formatea una lista de artículos en texto legible para compartir.
 * @param {string} nombreLista   - Nombre de la lista
 * @param {Array}  comparados    - Artículos con precio conocido
 * @param {Array}  manuales      - Artículos manuales sin precio
 * @param {number} totalEstimado - Total calculado
 * @returns {string} Texto formateado listo para compartir
 */
export function formatearListaParaCompartir(nombreLista, comparados, manuales, totalEstimado) {
    const lineas = [];

    lineas.push(`📋 Lista: ${nombreLista}`);
    lineas.push("─".repeat(30));

    if (comparados.length > 0) {
        lineas.push("🧮 Comparados:");
        comparados.forEach((a) => {
            const precio = a.precio_unitario ? ` — ${a.precio_unitario.toFixed(2)}€/ud` : "";
            const super_ = a.supermercado ? ` (${a.supermercado})` : "";
            const check  = a.comprado ? "✔️" : "☐";
            lineas.push(`  ${check} ${a.nombre} x${a.cantidad} ${a.unidad}${precio}${super_}`);
        });
    }

    if (manuales.length > 0) {
        if (comparados.length > 0) lineas.push("");
        lineas.push("✍️ Manuales:");
        manuales.forEach((a) => {
            const check = a.comprado ? "✔️" : "☐";
            lineas.push(`  ${check} ${a.nombre} x${a.cantidad} ${a.unidad}`);
        });
    }

    if (totalEstimado > 0) {
        lineas.push("─".repeat(30));
        lineas.push(`💰 Total estimado: ${totalEstimado.toFixed(2)}€`);
    }

    lineas.push("");
    lineas.push("Compartido desde ComPrara 🛒");

    return lineas.join("\n");
}

/**
 * Formatea una selección del comparador para compartir.
 * @param {Array}  items  - Items seleccionados en el comparador
 * @param {number} total  - Total calculado
 * @returns {string} Texto formateado
 */
export function formatearSeleccionParaCompartir(items, total) {
    const lineas = [];

    lineas.push("🛒 Mi selección de ComPrara");
    lineas.push("─".repeat(30));

    items.forEach((it) => {
        const subtotal = (it.precio * it.cantidad).toFixed(2);
        lineas.push(`☐ ${it.nombre_catalogo} x${it.cantidad} — ${subtotal}€ (${it.nombre_tienda})`);
    });

    lineas.push("─".repeat(30));
    lineas.push(`💰 Total: ${total.toFixed(2)}€`);
    lineas.push("");
    lineas.push("Compartido desde ComPrara 🛒");

    return lineas.join("\n");
}